import { Skeleton } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router';

import {
  agentLogoOf,
  brandingAgentOf,
  useGetGeneralDataQuery,
  useIsAuthenticated,
} from '@/features/auth';
import { cn } from '@/shared/lib/cn';

/** Bounds every mark that can occupy the slot — agent logo, Waive fallback and
 *  the skeleton that stands in for both — to one height, so the header does not
 *  resize as branding resolves. */
const MARK_BOX = 'h-9.5 shrink-0 sm:h-11';

/**
 * The portal's mark, which is the *agent's* mark.
 *
 * This product is white-labelled: a director reaches it through whichever
 * registered agent holds their companies, and the header wears that agent's
 * logo. Waive's own mark is the fallback, not the default — an agent who never
 * uploaded one, a director whose record carries no agent, and a logo URL that
 * fails to load all land there.
 *
 * Two things are layered here, and only one of them varies. "Director Portal"
 * is what the product IS, so it is always on screen as real text, whoever's
 * mark sits beside it and whether or not that mark has arrived. The mark slot
 * is the part that resolves: a skeleton while we are finding out, then the
 * agent's logo or Waive's. Painting a mark we may be about to replace is a
 * visible misbrand on first load, so the skeleton covers both waits — the
 * branding request, and the agent image decoding afterwards.
 */
export const HeaderLogo = () => {
  // Skipped until there is a session: the header also renders over the login
  // screen, where this request would 401. An unbranded header there is correct
  // — the agent is not known until the director is.
  const isAuthenticated = useIsAuthenticated();
  const { data, isLoading } = useGetGeneralDataQuery(undefined, { skip: !isAuthenticated });
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const logo = agentLogoOf(data);
  const agentName = brandingAgentOf(data)?.name ?? '';

  // A remote logo can 404 or be pulled after upload. Falling back on `error`
  // is what keeps the header from rendering a broken image on every screen.
  const showAgentLogo = logo !== undefined && !failed;
  // Only the branding request is a wait. An unauthenticated header has nothing
  // to look up, so it goes straight to the fallback mark rather than sitting on
  // a skeleton that would never resolve.
  const isResolving = isAuthenticated && isLoading;

  return (
    <Link to="/" className="flex max-w-fit min-w-0 flex-1 items-center gap-2.5 no-underline">
      {isResolving ? (
        <HeaderLogoSkeleton />
      ) : showAgentLogo ? (
        <>
          {/* The image stays mounted while hidden — it has to load to fire
              either handler — with the skeleton holding its box until it does. */}
          {!loaded && <HeaderLogoSkeleton />}
          <img
            src={logo}
            // An agent logo is an arbitrary image whose own dimensions are
            // unknown, so it is bounded by height and left to keep its aspect
            // ratio rather than given a width. The name is carried by the
            // link's accessible name below, so this is marked decorative
            // rather than repeating it to a screen reader.
            alt=""
            onLoad={() => {
              setLoaded(true);
            }}
            onError={() => {
              setFailed(true);
            }}
            className={cn(
              MARK_BOX,
              'w-auto max-w-32 object-contain sm:max-w-40',
              !loaded && 'hidden',
            )}
          />
        </>
      ) : (
        <img
          src="/waive-logo.png"
          // Decorative for the same reason the agent's logo is: the link states
          // where it goes once, below.
          alt=""
          // WORKAROUND: the only Waive asset we have is the full lockup, whose
          // artwork already reads "waive DIRECTOR PORTAL". Those words are now
          // permanent text beside the mark, so showing the lockup whole would
          // print them twice. Cropping to the wordmark's top band hides the
          // baked-in line. The correct fix is a standalone `waive-mark.png`
          // with no text in it; this goes away the moment that asset exists.
          className={cn(MARK_BOX, 'w-22 object-cover object-top sm:w-24')}
        />
      )}

      {/* What the product IS. Constant at every width: it does not belong to
          the agent, so it neither waits on their branding nor disappears behind
          it — and a phone is where a director is least likely to know what they
          have opened. Held to two tight lines against a hairline rule so it
          reads as a qualifier to whichever mark sits beside it rather than
          competing with it. */}
      <span aria-hidden className="h-6 shrink-0 border-l border-divider" />
      <span className="min-w-0 text-[11px] leading-[1.15] font-semibold tracking-wide text-content-muted uppercase">
        Director
        <br />
        Portal
      </span>

      {/* The link's whole accessible name, given once. Without it the link is
          just a mark and never says where it goes. */}
      <span className="sr-only">
        {agentName === '' ? 'Director Portal — home' : `${agentName} Director Portal — home`}
      </span>
    </Link>
  );
};

/** The mark's box, held while we find out whose mark goes in it. */
const HeaderLogoSkeleton = () => (
  <Skeleton
    variant="rounded"
    // Announced rather than silent: the header's one persistent element is
    // absent for as long as this shows.
    role="status"
    aria-label="Loading logo"
    className={cn(MARK_BOX, 'w-22 rounded-xl sm:w-24')}
  />
);
